/**
 * Authenticated Layout
 *
 * Layout for authenticated pages with:
 * - BaseAppBar (navigation header)
 * - Snackbar notifications
 *
 * GlobalEventManager and UserFlowManager are rendered in RootLayout
 * to prevent unmounting during auth state transitions.
 *
 * This is the inner layout for /bbweb/* routes.
 */
import React, { useState, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Outlet } from '@tanstack/react-router';
import { Box } from '@mui/material';

import BaseAppBar from '../components/BaseAppBar/BaseAppBar';
import { SnackbarCustom } from '../components';
import { setErrMsg, setSuccessMsg } from '../redux/actions/snackbarActions';
import { useLocation } from '@tanstack/react-router';
import { useAuth } from '@bb/session-manager';
import { TrialExpiredInterstitial } from '../components/subscription';
import { getSubscriptionStatus } from '../requests/subscription';
import { SUBSCRIPTION_UPGRADE_ROUTE } from '../routes.config';

export default function AuthenticatedLayout() {
  const successMsg = useSelector((state) => state.Snackbar.successMsg, shallowEqual);
  const errMsg = useSelector((state) => state.Snackbar.errMsg, shallowEqual);

  const dispatch = useDispatch();

  const setSuccess = (value) => dispatch(setSuccessMsg(value));
  const setErr = (value) => dispatch(setErrMsg(value));

  const { bbAxios } = useAuth();
  const { pathname } = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    getSubscriptionStatus()
      .then((data) => setSubscriptionStatus(data?.status))
      .catch(() => {}); // non-fatal — don't block the layout on failure
  }, [bbAxios]);

  const isTrialExpired = subscriptionStatus === 'trialing_expired' && pathname !== SUBSCRIPTION_UPGRADE_ROUTE;

  return (
    <Box sx={{ height: '100%' }}>
      <BaseAppBar />

      {isTrialExpired && <TrialExpiredInterstitial />}

      <Box sx={{ height: '100vh', paddingTop: '64px' }}>
        <Outlet />
      </Box>

      <SnackbarCustom severity="warning" msg={errMsg} setMsg={setErr} />
      <SnackbarCustom severity="success" msg={successMsg} setMsg={setSuccess} />
    </Box>
  );
}
